// CompatibilityTab.tsx
import React, { useState } from "react";
import { Heart } from "lucide-react";
import AstriaVietnamShell, {
  SHELL_COLORS_ASTRIA_VIETNAM,
} from "../AstriaVietnamShell";
import {
  AstriaVietnamResultCard,
  AstriaVietnamFieldBlock,
  AstriaVietnamLoadingState,
  AstriaVietnamErrorState,
  AstriaVietnamChatCta,
  CompatibilityScoreBadge,
} from "../AstriaVietnamCard";
import {
  WizardProgress,
  WizardStep,
  WizardNav,
  WizardChips,
  WizardDateFields,
  WizardDateFieldsValue,
  formatDateFieldsValue,
} from "../AstriaVietnamWizard";
import { useAstriaVietnamTab } from "../useAstriaVietnamTab";
import { AstriaVietnamCompatibilityData } from "../../../../contexts/ChatContext";
import { useAstriaVietnamLanguage } from "../astriaVietnamLanguage";
import { ASTRIA_VIETNAM_COPY } from "../astriaVietnamCopy";

// SubCategory name must contain "Compatibility" — matches
// resolveVietnamSubcategoryEntry in
// backend/helper/vietnam/astriaVietnamPromptService.js. Partner details
// are sent as labeled lines in userMessage (parsePartnerDetailsFromMessage
// looks for "Partner's Date of Birth:" / "Partner's Birth Hour:" — birth
// HOUR, not time-of-day, unlike India's compatibility lane).
const SUBCATEGORY_NAME = "Compatibility";
const TOTAL_STEPS = 2;

const EMPTY_DATE: WizardDateFieldsValue = {
  day: "",
  month: "",
  year: "",
  hourChi: null,
};

const CompatibilityTab: React.FC = () => {
  const [lang, setLang] = useAstriaVietnamLanguage();
  const copy = ASTRIA_VIETNAM_COPY[lang];
  const t = copy.compatibility;
  const shared = copy.shared;

  const { loading, error, message, send, reset, goToChat } =
    useAstriaVietnamTab({ subCategoryName: SUBCATEGORY_NAME, language: lang });

  const [step, setStep] = useState(1);
  const [selfDate, setSelfDate] = useState<WizardDateFieldsValue>(EMPTY_DATE);
  const [gender, setGender] = useState<string | null>(null);
  const [partnerDate, setPartnerDate] = useState<WizardDateFieldsValue>(EMPTY_DATE);

  const started = !!message || loading || !!error;

  const normalizedSelfDob = formatDateFieldsValue(selfDate);
  const normalizedPartnerDob = formatDateFieldsValue(partnerDate);
  const step1Valid = !!normalizedSelfDob && !!selfDate.hourChi && !!gender;
  const step2Valid = !!normalizedPartnerDob;

  const buildMessage = () => {
    // Only the partner's details go in the message text — self DOB is sent
    // via vietnamWizard (see useAstriaVietnamTab.ts / backend's
    // isAstriaVietnam dispatch, which now prefers vietnamWizard.dob over
    // the generic free-text DOB extractor precisely so a message
    // containing two dates never gets them mixed up).
    const lines = [
      "Read our Tử Vi compatibility (relationship energy).",
      `Partner's Date of Birth: ${normalizedPartnerDob}.`,
    ];
    if (partnerDate.hourChi) {
      lines.push(`Partner's Birth Hour: ${partnerDate.hourChi}.`);
    } else {
      lines.push("Partner's Birth Hour: Not provided.");
    }
    lines.push("Please reflect gently on our connection.");
    return lines.join("\n");
  };

  const handleSubmit = () => {
    send(buildMessage(), {
      dob: normalizedSelfDob || undefined,
      dob_hour: selfDate.hourChi || undefined,
      gender: gender || undefined,
    });
  };

  const compatibilityData = message?.astriaVietnamData as
    | AstriaVietnamCompatibilityData
    | undefined;

  if (started) {
    return (
      <AstriaVietnamShell active="compatibility" lang={lang} onLangChange={setLang}>
        <div style={{ marginBottom: 20 }}>
          <div
            style={{
              fontSize: 20,
              fontWeight: 700,
              color: SHELL_COLORS_ASTRIA_VIETNAM.titleColor,
              marginBottom: 4,
            }}
          >
            {t.tabLabel}
          </div>
          <p style={{ fontSize: 13.5, color: SHELL_COLORS_ASTRIA_VIETNAM.mutedColor }}>
            {t.tabSublabel} — {t.resultTitle}
          </p>
        </div>

        {loading && <AstriaVietnamLoadingState label={t.loading} />}

        {!loading && error && (
          <AstriaVietnamErrorState
            message={error}
            onRetry={handleSubmit}
            retryLabel={shared.tryAgain}
          />
        )}

        {!loading && !error && compatibilityData && (
          <>
            <AstriaVietnamResultCard
              icon={<Heart className="w-5 h-5" />}
              title={t.resultTitle}
              subtitle={t.tabLabel}
              shareText={`${t.resultTitle}\n\n${compatibilityData.rhythm_between}\n\n${compatibilityData.connection_path}`}
            >
              <CompatibilityScoreBadge
                score={compatibilityData.compatibility_score}
                label={t.scoreLabel}
              />
              <AstriaVietnamFieldBlock label={t.fields.rhythmBetween} value={compatibilityData.rhythm_between} />
              <AstriaVietnamFieldBlock label={t.fields.harmonyLevel} value={compatibilityData.harmony_level} />
              <AstriaVietnamFieldBlock label={t.fields.frictionPoint} value={compatibilityData.friction_point} />
              <AstriaVietnamFieldBlock label={t.fields.connectionPath} value={compatibilityData.connection_path} />
            </AstriaVietnamResultCard>
            <button
              onClick={() => {
                reset();
                setStep(1);
                setSelfDate(EMPTY_DATE);
                setGender(null);
                setPartnerDate(EMPTY_DATE);
              }}
              className="text-xs font-semibold transition-colors"
              style={{ marginTop: 14, color: SHELL_COLORS_ASTRIA_VIETNAM.mutedColor }}
            >
              {shared.startNewReading}
            </button>
            <AstriaVietnamChatCta onClick={goToChat} label={shared.continueInChat} />
          </>
        )}

        {!loading && !error && !compatibilityData && message && (
          <AstriaVietnamResultCard
            icon={<Heart className="w-5 h-5" />}
            title={t.resultTitle}
            subtitle={t.tabLabel}
          >
            <p
              style={{
                fontSize: 15,
                lineHeight: 1.75,
                color: SHELL_COLORS_ASTRIA_VIETNAM.bodyColor,
                whiteSpace: "pre-wrap",
              }}
            >
              {message.content}
            </p>
          </AstriaVietnamResultCard>
        )}
      </AstriaVietnamShell>
    );
  }

  return (
    <AstriaVietnamShell active="compatibility" lang={lang} onLangChange={setLang}>
      <WizardProgress step={step} total={TOTAL_STEPS} stepLabel={shared.stepOf} />

      {step === 1 && (
        <WizardStep stepKey="self" question={t.step1Question} helper={t.step1Helper}>
          <div style={{ marginBottom: 18 }}>
            <WizardDateFields
              value={selfDate}
              onChange={setSelfDate}
              hourOptions={shared.dobHourOptions}
              hourLabel={shared.dobHourLabel}
              hourPlaceholder={shared.dobHourPlaceholder}
              dayPlaceholder={shared.dayPlaceholder}
              monthPlaceholder={shared.monthPlaceholder}
              yearPlaceholder={shared.yearPlaceholder}
            />
          </div>

          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              color: SHELL_COLORS_ASTRIA_VIETNAM.mutedColor,
              marginBottom: 8,
            }}
          >
            {shared.genderLabel}
          </div>
          <WizardChips options={shared.genderOptions} value={gender} onChange={setGender} />

          <WizardNav
            showBack={false}
            nextDisabled={!step1Valid}
            onNext={() => setStep(2)}
            nextLabel={shared.continueLabel}
            backLabel={shared.back}
          />
        </WizardStep>
      )}

      {step === 2 && (
        <WizardStep stepKey="partner" question={t.step2Question} helper={t.step2Helper}>
          <div style={{ marginBottom: 18 }}>
            <WizardDateFields
              value={partnerDate}
              onChange={setPartnerDate}
              hourOptions={shared.dobHourOptions}
              hourLabel={t.partnerDobHourPlaceholder}
              hourPlaceholder={t.partnerDobHourPlaceholder}
              dayPlaceholder={shared.dayPlaceholder}
              monthPlaceholder={shared.monthPlaceholder}
              yearPlaceholder={shared.yearPlaceholder}
            />
          </div>

          <WizardNav
            onBack={() => setStep(1)}
            nextDisabled={!step2Valid}
            onNext={handleSubmit}
            nextLabel={t.revealButton}
            backLabel={shared.back}
          />
        </WizardStep>
      )}
    </AstriaVietnamShell>
  );
};

export default CompatibilityTab;
